import { useState, useEffect, useCallback } from 'react'
import { Contents } from '../../components/layouts'
import { ReplyStatusMatrix } from './ReplyStatusMatrix'
import { Button } from '@progress/kendo-react-buttons'
import { StackLayout } from '@progress/kendo-react-layout'
import { HomeInformationInput } from './HomeInformationInput'
import { CommonActions, useAppDispatch, useAppSelector } from '../../stores'
import { Authority } from '../../gateway/system/Authority'
import { GetMessageGateway } from '../../gateway/home/GetMessageGateway'
import { ExpenseReplyCnt, HomeMsg, SupplierReply } from '@gateway.Home'
import { HomeInformationDisplay } from './HomeInformationDisplay'
import Constants from '../../data/Constants.json'
import { ErrorMessage, ErrorMessages } from '../../components/parts'
import { DateSettingInput } from './DateSettingInput'
import { YearSettingInput } from './YearSettingInput'

export const Home = () => {
  const dispatch = useAppDispatch()
  const [errorMessages, setErrorMessages] = useState<ErrorMessage[]>([])

  // 年度の編集ダイアログのステータス
  const [openYearEditDialog, setOpenYearEditDialog] = useState(false)
  // 日程の編集ダイアログのステータス
  const [openDateEditDialog, setOpenDateEditDialog] = useState(false)
  // 社内向けお知らせの編集ダイアログのステータス
  const [openMsgEditDialog, setOpenMsgEditDialog] = useState(false)
  // お知らせの取得したデータ
  const [msgList, setMsgList] = useState<HomeMsg[]>([])
  // 業者回答状況
  const [supplierReplyList, setSupplierReplyList] = useState<SupplierReply[]>(
    []
  )
  // 業者回答状況テーブルデータ
  const [tableData, setTableData] = useState<ExpenseReplyCnt[]>([])

  // 業者が担当する費目リスト
  const [supplierExpenseList, setSupplierExpenseList] = useState<string[]>([])

  const common = useAppSelector((x) => x.common)
  const displayStartYear = useAppSelector((x) => x.common.displayStartYear)

  // テキスト区分を設定します
  const codes = common.initialData.codeNames('TE01')

  // 支店情報のリストを取得
  const branches = common.initialData.codeNames('SI01')

  // 費目情報のリストを取得
  const expenses = common.initialData.codeNames('HI01')

  const userProp = useAppSelector((x) => x.common.userProp)

  // 支店コードの取得
  const branchCd = userProp?.branchCode ?? ''

  // ログインユーザが工務担当者または建材部担当者の場合
  let branchCdList = Constants.kantoFourBranches

  // ログインユーザが支店担当者の場合
  if (userProp?.authority === Authority.Branch) {
    branchCdList = [branchCd]
  }

  // 業者全体向けお知らせ用のテキスト区分
  const supplierTxtKbn = codes.find((code) => code.CODE === '02')?.TXT1 ?? ''

  /**
   * 年度の編集ダイアログを開くまたは閉じる
   */
  const toggleYearEditDialog = () => {
    setOpenYearEditDialog(!openYearEditDialog)
  }

  /**
   * 日程の編集ダイアログを開くまたは閉じる
   */
  const toggleDateEditDialog = () => {
    setOpenDateEditDialog(!openDateEditDialog)
  }

  /**
   * 社内向けお知らせの編集ダイアログを開くまたは閉じる
   */
  const toggleMsgEditDialog = () => {
    setOpenMsgEditDialog(!openMsgEditDialog)
  }

  /**
   * 業者全体向けのお知らせ
   */
  const getSupplierMsg = () => {
    return msgList.find((msg) => msg.txtKbn === supplierTxtKbn)?.txt ?? ''
  }

  /**
   * 費目分のお知らせの取得
   */
  const getExpenseMsg = (txtKbn: string) => {
    return msgList.find((msg) => msg.txtKbn === txtKbn)?.txt ?? ''
  }

  /**
   * ログインユーザが工務担当者または建材部担当者、支店担当者の場合
   * 大成社内向けのお知らせの取得
   */
  const fetchMessage = useCallback(() => {
    // テキスト1を取得
    const txtKbn = codes.find((code) => code.CODE === '01')?.TXT1 ?? ''

    GetMessageGateway.getMessage(txtKbn, [], branchCdList)
      .then((res) => {
        if (!res.error) {
          setMsgList(res.data.msgList)
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }, [codes, userProp?.authority])

  /**
   * 業者回答状況の取得と表データの構築
   */
  const fetchSupplierReply = useCallback(() => {
    // テーブルデータの初期化
    const expenseReplyCntList: ExpenseReplyCnt[] = branches.map((branch) => {
      const expenseReplyCnt: ExpenseReplyCnt = {
        branchCd: branch.CODE ?? '',
        branchNm: branch.NAME ?? ''
      }
      expenses.forEach((expense) => {
        if (expense.CODE !== undefined) expenseReplyCnt[expense.CODE] = 0
      })
      return expenseReplyCnt
    })

    // 業者回答状況の取得
    GetMessageGateway.getSupplierReply()
      .then((res) => {
        if (!res.error) {
          setSupplierReplyList(res.data.supplierReplyList)

          if (res.data.supplierReplyList.length > 0) {
            expenseReplyCntList.forEach((expenseReplyCnt) => {
              res.data.supplierReplyList.forEach((item) => {
                if (expenseReplyCnt.branchCd === item.branchCd) {
                  // 表データの移入
                  expenseReplyCnt[item.expenseCd] = item.replyCnt
                }
              })
            })
          }
        } else {
          if (Array.isArray(res.messages) && res.messages.length > 0) {
            const errorMessages = res.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})

    setTableData(expenseReplyCntList)
  }, [branches, expenses])

  /**
   * 業者表示用のメッセージ取得
   */
  const fetchSupplierMessage = useCallback((expenseList: string[]) => {
    // 費目分のテキスト区分を取得
    const expenseTxtKbnList = codes
      .filter((code) => expenseList.some((expense) => expense === code.TXT2))
      .map((code) => code.TXT1 ?? '')

    const branchCdList = branches.map((item) => item.CODE ?? '')
    // APIで取得したデータをもとにステートを作成する
    GetMessageGateway.getMessage(supplierTxtKbn, expenseTxtKbnList, branchCdList)
      .then((result) => {
        if (!result.error) {
          setMsgList(result.data.msgList)
        } else {
          if (Array.isArray(result.messages) && result.messages.length > 0) {
            const errorMessages = result.messages.map<ErrorMessage>((message) => {
              return {
                id: message.id,
                content: message.message
              }
            })
            setErrorMessages(errorMessages)
          }
        }
      })
      .finally(() => {})
  }, [])

  /**
   * ページ初期化
   * お知らせの取得
   * 業者回答状況の取得
   */
  useEffect(() => {
    // ストアの表示開始年度が未設定の場合、現在の年度で初期化する
    if (displayStartYear === '') {
      // 現在の日付を基に年度を計算（4月始まり）
      const today = new Date()
      const currentYear = today.getFullYear()
      const currentMonth = today.getMonth()
      // 4月(月番号3)より前なら前年度扱い
      const fiscalYear = currentMonth < 3 ? currentYear - 1 : currentYear
      dispatch(CommonActions.setDisplayStartYear(String(fiscalYear)))
    }

    // ログインユーザが工務担当者または建材部担当者、支店担当者の場合
    if (
      userProp?.authority === Authority.Builder ||
      userProp?.authority === Authority.Material ||
      userProp?.authority === Authority.Branch
    ) {
      fetchMessage()
      fetchSupplierReply()
    }

    // ログインユーザが業者の場合
    if (userProp?.authority === Authority.Supplier) {
      // 業者コードを取得
      const supplierCd = userProp?.supplier?.supplierCode ?? ''

      // 業者に紐づく費目データを抽出する
      GetMessageGateway.getHomeExpense(supplierCd)
        .then((result) => {
          if (!result.error) {
            setSupplierExpenseList(result.data.expenseList)
            fetchSupplierMessage(result.data.expenseList)
          } else {
            if (Array.isArray(result.messages) && result.messages.length > 0) {
              const errorMessages = result.messages.map<ErrorMessage>((message) => {
                return {
                  id: message.id,
                  content: message.message
                }
              })
              setErrorMessages(errorMessages)
            }
          }
        })
        .finally(() => {})
    }
  }, [])

  // 業者です
  const isSupplier = userProp?.authority === Authority.Supplier
  // 工務担当者または建材部担当者、支店担当者です
  const isOthers =
    userProp?.authority === Authority.Branch ||
    userProp?.authority === Authority.Builder ||
    userProp?.authority === Authority.Material

  // ログインユーザが工務権限か判定
  const isBuilder = userProp?.authority === Authority.Builder

  return (
    <Contents>
      {isOthers && (
        <>
          {branchCdList.map((branchCd) => (
            <HomeInformationDisplay
              key={branchCd}
              header={`【${branches.find((branch) => branch.CODE === branchCd)?.NAME ?? ''}】大成社内向けのお知らせ`}
              information={msgList.length > 0 ? msgList.filter((msg) => msg.txtId === branchCd)[0].txt ?? '' : ''}
              />
          ))}
          <ReplyStatusMatrix
            supplierReplyList={supplierReplyList}
            tableData={tableData}
          />
          <StackLayout
            gap={5}
            align={{ horizontal: 'end' }}
            style={{ marginTop: 10 }}
          >
            {isBuilder && (
              <Button
                onClick={toggleYearEditDialog}
                themeColor="primary"
                className="pcy-button-short"
              >
                年度
              </Button>
            )}
            <Button
              onClick={toggleDateEditDialog}
              themeColor="primary"
              className="pcy-button-short"
            >
              日程
            </Button>
            <Button
              onClick={toggleMsgEditDialog}
              themeColor="primary"
              className="pcy-button-short"
            >
              掲示板
            </Button>
          </StackLayout>
          {openYearEditDialog && (
            <YearSettingInput
              onCloseEditWindow={toggleYearEditDialog}
              refetchHomeInformation={() => {
                fetchMessage()
                fetchSupplierReply()
              }}
              setErrorMessages={setErrorMessages}
            />
          )}
          {openDateEditDialog && (
            <DateSettingInput
              onCloseEditWindow={toggleDateEditDialog}
              refetchHomeInformation={() => {
                fetchMessage()
                fetchSupplierReply()
              }}
              setErrorMessages={setErrorMessages}
            />
          )}
          {openMsgEditDialog && (
            <HomeInformationInput
              onCloseEditWindow={toggleMsgEditDialog}
              refetchHomeInformation={() => {
                fetchMessage()
                fetchSupplierReply()
              }}
              setErrorMessages={setErrorMessages}
            />
          )}
        </>
      )}
      {isSupplier && (
        <>
          <HomeInformationDisplay
            header={'業者全体向けのお知らせ'}
            information={getSupplierMsg()}
          />
          {codes
            .filter((code) => code.CODE !== '01' &&
                              code.CODE !== '02' &&
                              code.TXT2 !== undefined &&
                              supplierExpenseList.includes(code.TXT2))
            .map((code) => (
              <HomeInformationDisplay
                key={code.CODE}
                header={code.NAME ?? ''}
                information={getExpenseMsg(code.TXT1 ?? '')}
              />
            ))}
        </>
      )}
      <ErrorMessages type="dialog" messages={errorMessages} />
    </Contents>
  )
}

export default Home
